import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, AlertCircle, CheckCircle, Eye, EyeOff, Activity, ShieldCheck } from 'lucide-react';
import { API_URL, API_KEY } from '../config';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display:ital@0;1&display=swap');

  :root {
    --teal:#0EA5A0; --teal-d:#0B8A85; --teal-dim:rgba(14,165,160,0.10); --teal-dim2:rgba(14,165,160,0.06);
    --sky:#38BDF8; --navy:#0F2744;
    --bg:#F4F8FB; --white:#FFFFFF;
    --border:rgba(14,165,160,0.18); --border2:rgba(14,165,160,0.28);
    --text1:#0F2744; --text2:#476585; --text3:#7A9AB5;
    --err:#DC2626; --err-bg:rgba(220,38,38,0.07);
    --suc:#059669; --suc-bg:rgba(5,150,105,0.08);
    --shadow:0 4px 24px rgba(14,165,160,0.10),0 1px 4px rgba(15,39,68,0.08);
    --shadow-btn:0 4px 18px rgba(14,165,160,0.35);
    --radius:18px; --radius-sm:8px;
    --fd:'DM Serif Display',Georgia,serif; --fb:'DM Sans',system-ui,sans-serif;
    --transition:all 0.22s cubic-bezier(0.4,0,0.2,1);
  }

  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{font-family:var(--fb);background:var(--bg);color:var(--text1);-webkit-font-smoothing:antialiased}

  .pw-root{
    min-height:100vh; display:flex; align-items:center; justify-content:center; padding:40px 20px;
    background:linear-gradient(135deg,#EBF5FB 0%,#F4F8FB 40%,#EDF7F4 100%);
    position:relative;
  }
  .pw-root::before{
    content:''; position:fixed; inset:0;
    background-image:radial-gradient(rgba(14,165,160,0.11) 1px,transparent 1px);
    background-size:28px 28px; pointer-events:none; z-index:0;
  }

  .pw-card{
    position:relative; z-index:1;
    background:var(--white); border:1px solid var(--border);
    border-radius:var(--radius); padding:44px 40px;
    width:100%; max-width:440px;
    box-shadow:var(--shadow);
    animation:fadeUp .5s cubic-bezier(0.4,0,0.2,1) both;
  }
  @keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}

  .pw-logo{margin-bottom:28px}
  .pw-logo img{height:32px;width:auto;display:block}
  .logo-fallback{display:flex;align-items:center;gap:9px}
  .logo-icon{width:30px;height:30px;border-radius:8px;background:var(--teal);display:flex;align-items:center;justify-content:center}
  .logo-name{font-family:var(--fd);font-size:.95rem;color:var(--navy)}

  .btn-back{display:inline-flex;align-items:center;gap:7px;background:#F4F8FB;border:1px solid #D4E4EF;border-radius:var(--radius-sm);padding:7px 14px;font-family:var(--fb);font-size:.78rem;font-weight:500;color:var(--text2);cursor:pointer;transition:var(--transition);margin-bottom:28px}
  .btn-back:hover{border-color:var(--teal);color:var(--teal);background:var(--teal-dim2)}

  .icon-ring{width:52px;height:52px;border-radius:14px;background:var(--teal-dim);border:1px solid var(--border2);display:flex;align-items:center;justify-content:center;margin-bottom:18px}

  .pw-title{font-family:var(--fd);font-size:1.7rem;color:var(--navy);margin-bottom:8px}
  .pw-sub{font-size:.84rem;color:var(--text2);margin-bottom:28px;line-height:1.65}

  .alert{display:flex;align-items:flex-start;gap:10px;padding:12px 15px;border-radius:var(--radius-sm);font-size:.8125rem;margin-bottom:18px;line-height:1.5}
  .alert-error{background:var(--err-bg);border:1px solid rgba(220,38,38,0.2);color:var(--err)}
  .alert-success{background:var(--suc-bg);border:1px solid rgba(5,150,105,0.2);color:var(--suc)}

  .field{margin-bottom:15px}
  .field-label{display:block;font-size:.69rem;font-weight:600;letter-spacing:.07em;text-transform:uppercase;color:var(--text3);margin-bottom:6px}
  .field-wrap{position:relative}
  .field-input{width:100%;background:#F8FAFC;border:1px solid #D4E4EF;border-radius:var(--radius-sm);padding:11px 13px;font-family:var(--fb);font-size:.9rem;color:var(--text1);outline:none;transition:var(--transition);appearance:none}
  .field-input::placeholder{color:#A8C0D0}
  .field-input:focus{border-color:var(--teal);background:#fff;box-shadow:0 0 0 3px rgba(14,165,160,0.14)}
  .field-input.has-icon{padding-right:44px}
  .field-icon-btn{position:absolute;right:12px;top:50%;transform:translateY(-50%);background:none;border:none;color:var(--text3);cursor:pointer;display:flex;align-items:center;padding:2px;transition:color .15s}
  .field-icon-btn:hover{color:var(--teal)}

  .str-bar{display:flex;gap:3px;margin-top:6px}
  .str-seg{flex:1;height:3px;border-radius:2px;background:#E2EDF5;transition:background .3s}
  .str-seg.weak{background:var(--err)}
  .str-seg.fair{background:#D97706}
  .str-seg.strong{background:var(--suc)}

  .btn-primary{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;padding:13px;background:var(--teal);border:none;border-radius:var(--radius-sm);font-family:var(--fb);font-size:.9375rem;font-weight:600;color:#fff;cursor:pointer;box-shadow:var(--shadow-btn);transition:var(--transition);letter-spacing:.01em}
  .btn-primary:hover:not(:disabled){background:var(--teal-d);transform:translateY(-1px);box-shadow:0 6px 24px rgba(14,165,160,.45)}
  .btn-primary:disabled{opacity:.6;cursor:not-allowed}

  .spinner{display:inline-block;width:18px;height:18px;border:2px solid rgba(255,255,255,.4);border-top-color:#fff;border-radius:50%;animation:spin .7s linear infinite}
  @keyframes spin{to{transform:rotate(360deg)}}

  .success-state{display:flex;flex-direction:column;align-items:center;text-align:center;padding:16px 0;gap:16px}
  .success-ring{width:68px;height:68px;border-radius:50%;background:var(--suc-bg);border:2px solid rgba(5,150,105,0.3);display:flex;align-items:center;justify-content:center;animation:popIn .4s cubic-bezier(.34,1.56,.64,1) both}
  @keyframes popIn{from{transform:scale(.5);opacity:0}to{transform:scale(1);opacity:1}}
  .success-title{font-family:var(--fd);font-size:1.35rem;color:var(--navy)}
  .success-body{font-size:.84rem;color:var(--text2);line-height:1.65;max-width:290px}

  .btn-link{background:none;border:none;color:var(--text2);cursor:pointer;font-size:.8rem;font-family:var(--fb);padding:0}
  .btn-link:hover{color:var(--teal)}

  @media(max-width:520px){.pw-card{padding:32px 24px}}
`;

function getStrength(pw) {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}

function StrengthBar({ password }) {
  const score = getStrength(password);
  const cls = score <= 1 ? 'weak' : score <= 2 ? 'fair' : 'strong';
  return (
    <div className="str-bar">
      {[1,2,3,4].map(i => <div key={i} className={`str-seg ${i <= score ? cls : ''}`} />)}
    </div>
  );
}

function LogoBlock() {
  return (
    <div className="pw-logo">
      <img src="/logo.png" alt="Cytometrix"
        onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
      <div className="logo-fallback" style={{ display:'none' }}>
        <div className="logo-icon"><Activity size={15} color="#fff" strokeWidth={2.5} /></div>
        <span className="logo-name">Cytometrix</span>
      </div>
    </div>
  );
}

export function ForgotPasswordScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handle = async (e) => {
    e.preventDefault(); setError('');
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Please enter a valid email address.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) setSent(true);
      else setError(data.message || 'Something went wrong.');
    } catch { setError('Cannot connect to server.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <style>{css}</style>
      <div className="pw-root">
        <div className="pw-card">
          <LogoBlock />
          {!sent && (
            <button className="btn-back" onClick={() => navigate('/login')}>
              <ArrowLeft size={13} />Back to Login
            </button>
          )}
          {sent ? (
            <div className="success-state">
              <div className="success-ring"><CheckCircle size={30} color="var(--suc)" strokeWidth={2} /></div>
              <div>
                <p className="success-title">Check your inbox</p>
                <p className="success-body">We've sent a reset link to <strong style={{color:'var(--navy)'}}>{email}</strong>. Follow the instructions to create a new password.</p>
              </div>
              <button className="btn-primary" style={{marginTop:8}} onClick={() => navigate('/login')}>Back to Login</button>
            </div>
          ) : (
            <>
              <div className="icon-ring"><Mail size={22} color="var(--teal)" /></div>
              <h2 className="pw-title">Forgot Password</h2>
              <p className="pw-sub">Enter the email associated with your clinical account and we'll send a secure reset link.</p>
              {error && <div className="alert alert-error"><AlertCircle size={15} style={{flexShrink:0,marginTop:1}} />{error}</div>}
              <form onSubmit={handle}>
                <div className="field">
                  <label className="field-label">Email Address</label>
                  <input className="field-input" type="email" placeholder="your@hospital.com" value={email} onChange={e => setEmail(e.target.value)} />
                </div>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? <span className="spinner" /> : 'Send Reset Link'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export function ResetPasswordScreen() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showCfm, setShowCfm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const token = new URLSearchParams(window.location.search).get('token') || '';

  const handle = async (e) => {
    e.preventDefault(); setError('');
    if (!password || !confirm) { setError('All fields are required.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/reset-password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok) { setSuccess(true); setTimeout(() => navigate('/login'), 2500); }
      else setError(data.message || 'Reset failed.');
    } catch { setError('Cannot connect to server.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <style>{css}</style>
      <div className="pw-root">
        <div className="pw-card">
          <LogoBlock />
          {success ? (
            <div className="success-state">
              <div className="success-ring"><ShieldCheck size={30} color="var(--suc)" strokeWidth={2} /></div>
              <div>
                <p className="success-title">Password Updated</p>
                <p className="success-body">Your password has been changed successfully. Redirecting you to sign in…</p>
              </div>
            </div>
          ) : (
            <>
              <div className="icon-ring"><ShieldCheck size={22} color="var(--teal)" /></div>
              <h2 className="pw-title">Reset Password</h2>
              <p className="pw-sub">Choose a strong new password for your clinical account.</p>
              {error && <div className="alert alert-error"><AlertCircle size={15} style={{flexShrink:0,marginTop:1}} />{error}</div>}
              <form onSubmit={handle}>
                <div className="field">
                  <label className="field-label">New Password</label>
                  <div className="field-wrap">
                    <input className="field-input has-icon" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 8 characters" />
                    <button type="button" className="field-icon-btn" onClick={() => setShowPw(s => !s)} tabIndex={-1}>
                      {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  <StrengthBar password={password} />
                </div>
                <div className="field">
                  <label className="field-label">Confirm Password</label>
                  <div className="field-wrap">
                    <input className="field-input has-icon" type={showCfm ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat your password" />
                    <button type="button" className="field-icon-btn" onClick={() => setShowCfm(s => !s)} tabIndex={-1}>
                      {showCfm ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  {confirm && password !== confirm && <p style={{fontSize:'.72rem',color:'var(--err)',marginTop:5}}>Passwords do not match</p>}
                  {confirm && password === confirm && confirm.length > 0 && <p style={{fontSize:'.72rem',color:'var(--suc)',marginTop:5}}>Passwords match ✓</p>}
                </div>
                <button type="submit" className="btn-primary" disabled={loading || success}>
                  {loading ? <span className="spinner" /> : 'Reset Password'}
                </button>
              </form>
              <p style={{textAlign:'center',marginTop:18}}>
                <button className="btn-link" onClick={() => navigate('/login')}>← Return to login</button>
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
}